import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { EmailCampaignsMeta, API_BASE_URL } from '../data'
import { EmailCampaign, EmailCampaignsMetaProps } from '../data/types'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { EmailIcon, EditIcon, DeleteIcon, PlusIcon, DownloadIcon } from '../icons'
import { Card } from '../components/ui'
import { useAuth } from './auth/AuthContext'

type Props = {
    meta: EmailCampaignsMetaProps
}

const EmailCampaigns = (props: Props) => {
    const { meta } = props
    const navigate = useNavigate()
    const { user } = useAuth()
    const canDraftCampaigns = user?.permissions?.includes('manage_email_campaigns')
    const canApproveCampaigns = user?.permissions?.includes('approve_email_campaigns')
    const canExportRecipients = user?.role === 'admin'
    const [campaigns, setCampaigns] = useState<EmailCampaign[]>([])
    const [showPendingApprovalOnly, setShowPendingApprovalOnly] = useState(false)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [campaignToDelete, setCampaignToDelete] = useState<number | null>(null)
    const [isPolling, setIsPolling] = useState(false)

    // Decode HTML entities in preview text
    const decodePreview = (preview: string | null | undefined): string => {
        if (!preview) return ''
        const textarea = document.createElement('textarea')
        textarea.innerHTML = preview
        return textarea.value
    }

    useEffect(() => {
        fetchCampaigns()
    }, [])

    // Poll for updates if any campaigns are sending
    useEffect(() => {
        const hasSendingCampaigns = campaigns.some(c => c.status === 'sending')

        if (!hasSendingCampaigns) {
            return
        }

        // Poll every 3 seconds
        const intervalId = setInterval(() => {
            setIsPolling(true)
            fetchCampaigns().finally(() => setIsPolling(false))
        }, 3000)

        return () => clearInterval(intervalId)
    }, [campaigns])

    const fetchCampaigns = async () => {
        // Only show full-page loading on initial load, not during polling
        if (!isPolling && campaigns.length === 0) {
            setLoading(true)
        }
        setError(null)
        try {
            const response = await fetch(`${API_BASE_URL}/email-campaigns`, {
                credentials: 'include'
            })

            if (!response.ok) throw new Error('Failed to fetch campaigns')

            const result = await response.json()
            let data = result.value || result

            // Handle double-nested arrays
            if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                data = data[0]
            }

            setCampaigns(Array.isArray(data) ? data : [])
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorTitle)
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!campaignToDelete) return

        try {
            const response = await fetch(`${API_BASE_URL}/email-campaigns/${campaignToDelete}`, {
                method: 'DELETE',
                credentials: 'include'
            })

            if (!response.ok) throw new Error('Failed to delete campaign')

            setCampaigns(campaigns.filter(c => c.id !== campaignToDelete))
            setDeleteModalOpen(false)
            setCampaignToDelete(null)
        } catch (err) {
            console.error('Error deleting campaign:', err)
            alert('Failed to delete campaign')
        }
    }

    const handleExportRecipients = async (campaignId: number) => {
        try {
            const response = await fetch(`${API_BASE_URL}/email-campaigns/${campaignId}/recipients/export`, {
                credentials: 'include'
            })

            if (!response.ok) throw new Error('Failed to export recipients')

            // Get filename from Content-Disposition header or use default
            const contentDisposition = response.headers.get('Content-Disposition')
            let filename = `campaign-${campaignId}-recipients.csv`
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
                if (filenameMatch) {
                    filename = filenameMatch[1]
                }
            }

            // Create blob and trigger download
            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = filename
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
        } catch (err) {
            console.error('Error exporting recipients:', err)
            alert('Failed to export recipients')
        }
    }

    const getStatusBadge = (status: string) => {
        const statusConfig = {
            draft: { label: meta.statusDraft, color: 'bg-stone-200 text-stone-700' },
            test_sent: { label: meta.statusTestSent, color: 'bg-primary-200 text-primary-700' },
            pending_approval: { label: meta.statusPendingApproval, color: 'bg-warn-200 text-warn-700' },
            sending: { label: meta.statusSending, color: 'bg-warn-200 text-warn-700' },
            completed: { label: meta.statusCompleted, color: 'bg-success-200 text-success-700' },
            failed: { label: meta.statusFailed, color: 'bg-danger-200 text-danger-700' }
        }

        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft

        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                {config.label}
            </span>
        )
    }

    const getProgressBar = (campaign: EmailCampaign) => {
        if (campaign.total_recipients === 0) return null

        const percentage = Math.round((campaign.sent_count / campaign.total_recipients) * 100)

        return (
            <div className="w-full">
                <div className="flex justify-between text-xs text-stone-600 mb-1">
                    <span>{campaign.sent_count} / {campaign.total_recipients}</span>
                    <span>{percentage}%</span>
                </div>
                <div className="w-full bg-stone-200 rounded-full h-2">
                    <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                    />
                </div>
                {campaign.failed_count > 0 && (
                    <span className="text-xs text-danger-600 mt-1 block">
                        {campaign.failed_count} failed
                    </span>
                )}
            </div>
        )
    }

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr)
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const pendingApprovalCount = campaigns.filter(campaign => campaign.status === 'pending_approval').length
    const visibleCampaigns = showPendingApprovalOnly
        ? campaigns.filter(campaign => campaign.status === 'pending_approval')
        : campaigns

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <LoadingSpinner meta={{ loadingText: meta.loadingText }} />
            </div>
        )
    }

    return (
        <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-stone-800 dark:text-stone-100 flex items-center gap-3">
                                <EmailIcon className="w-8 h-8" />
                                {meta.title}
                            </h1>
                            <p className="text-stone-600 dark:text-stone-400 mt-2">
                                {meta.description}
                            </p>
                            {canApproveCampaigns && (
                                <p className="text-sm font-medium text-warn-700 dark:text-warn-300 mt-2">
                                    {meta.pendingApprovalCountLabel(pendingApprovalCount)}
                                </p>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                            {canApproveCampaigns && (
                                <button
                                    onClick={() => setShowPendingApprovalOnly(!showPendingApprovalOnly)}
                                    className={`px-4 py-2 rounded-lg border transition-colors ${showPendingApprovalOnly ? 'bg-warn-100 border-warn-300 text-warn-800 dark:bg-warn-900/30 dark:border-warn-700 dark:text-warn-200' : 'border-stone-300 text-stone-700 hover:bg-stone-100 dark:border-stone-600 dark:text-stone-300 dark:hover:bg-stone-800'}`}
                                >
                                    {showPendingApprovalOnly ? meta.showAllCampaignsLabel : meta.showPendingApprovalOnlyLabel}
                                </button>
                            )}
                            {canDraftCampaigns && (
                                <button
                                    onClick={() => navigate('/admin/email-campaigns/create')}
                                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                                >
                                    <PlusIcon className="w-5 h-5" />
                                    {meta.createButton}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Error State */}
                {error && (
                    <Card className="mb-6 border-danger-200 dark:border-danger-800">
                        <div className="p-4 text-danger-700 dark:text-danger-300">
                            <p className="font-semibold">{meta.errorTitle}</p>
                            <p className="text-sm">{error}</p>
                        </div>
                    </Card>
                )}

                {/* Campaigns List */}
                {visibleCampaigns.length === 0 ? (
                    <Card>
                        <div className="p-12 text-center">
                            <EmailIcon className="w-16 h-16 mx-auto text-stone-300 dark:text-stone-600 mb-4" />
                            <p className="text-stone-600 dark:text-stone-400 text-lg">
                                {showPendingApprovalOnly ? meta.noPendingApprovalCampaignsMessage : meta.noCampaignsMessage}
                            </p>
                            {canDraftCampaigns && !showPendingApprovalOnly && (
                                <button
                                    onClick={() => navigate('/admin/email-campaigns/create')}
                                    className="mt-4 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                                >
                                    {meta.createButton}
                                </button>
                            )}
                        </div>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {visibleCampaigns.map((campaign) => (
                            <Card key={campaign.id} className="hover:shadow-lg transition-shadow">
                                <div className="p-6">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="text-xl font-semibold text-stone-800 dark:text-stone-100">
                                                    {campaign.subject}
                                                </h3>
                                                {getStatusBadge(campaign.status)}
                                            </div>

                                            {campaign.body_preview && (
                                                <p className="text-stone-600 dark:text-stone-400 text-sm mb-4 line-clamp-2 whitespace-break-spaces">
                                                    {decodePreview(campaign.body_preview).substring(0, 100)}{decodePreview(campaign.body_preview).length > 100 ? '...' : ''}
                                                </p>
                                            )}

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                <div>
                                                    <p className="text-xs text-stone-500 dark:text-stone-500">
                                                        {meta.createdLabel}
                                                    </p>
                                                    <p className="text-sm text-stone-700 dark:text-stone-300">
                                                        {formatDate(campaign.created_at)}
                                                    </p>
                                                    {campaign.creator && (
                                                        <p className="text-xs text-stone-500 dark:text-stone-500">
                                                            by {campaign.creator.name}
                                                        </p>
                                                    )}
                                                </div>

                                                {campaign.total_recipients > 0 && (
                                                    <div>
                                                        <p className="text-xs text-stone-500 dark:text-stone-500 mb-2">
                                                            {meta.progressLabel}
                                                        </p>
                                                        {getProgressBar(campaign)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex gap-2 ms-4">
                                            {canExportRecipients && campaign.total_recipients > 0 && (
                                                <button
                                                    onClick={() => handleExportRecipients(campaign.id)}
                                                    className="p-2 text-stone-600 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg transition-colors"
                                                    title={meta.exportRecipientsButton}
                                                >
                                                    <DownloadIcon className="w-5 h-5" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => navigate(`/admin/email-campaigns/${campaign.id}/edit`)}
                                                className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                                                title={meta.editButton}
                                            >
                                                <EditIcon className="w-5 h-5" />
                                            </button>
                                            {campaign.status !== 'sending' && (
                                                <button
                                                    onClick={() => {
                                                        setCampaignToDelete(campaign.id)
                                                        setDeleteModalOpen(true)
                                                    }}
                                                    className="p-2 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded-lg transition-colors"
                                                    title={meta.deleteButton}
                                                >
                                                    <DeleteIcon className="w-5 h-5" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {deleteModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <Card className="max-w-md w-full mx-4">
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-stone-800 dark:text-stone-100 mb-2">
                                    {meta.deleteConfirmTitle}
                                </h3>
                                <p className="text-stone-600 dark:text-stone-400 mb-6">
                                    {meta.deleteConfirmMessage}
                                </p>
                                <div className="flex gap-3 justify-end">
                                    <button
                                        onClick={() => {
                                            setDeleteModalOpen(false)
                                            setCampaignToDelete(null)
                                        }}
                                        className="px-4 py-2 text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg transition-colors"
                                    >
                                        {meta.cancelButton}
                                    </button>
                                    <button
                                        onClick={handleDelete}
                                        className="px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 transition-colors"
                                    >
                                        {meta.confirmDeleteButton}
                                    </button>
                                </div>
                            </div>
                        </Card>
                    </div>
                )}
        </div>
    )
}

export default () => <EmailCampaigns meta={EmailCampaignsMeta} />
